/**
 * Case-checking rule set API (drafts, publish, versions).
 * System prompt is never returned or accepted — it stays server-side.
 */

import { apiRequest, apiRequestWithHeaders } from "../client";
import type { CaseRole } from "./index";

/** Scope rule-set admin calls to a specific review profile (Soderberg phases, etc.). */
export type RuleSetProfileScope = { reviewProfile: string };

function reviewProfileQuery(scope?: RuleSetProfileScope): string {
  if (!scope?.reviewProfile) return "";
  return `?review_profile=${encodeURIComponent(scope.reviewProfile)}`;
}

export interface RuleGroup {
  id: string;
  name: string;
  description: string | null;
  order: number;
}

export interface Rule {
  id: string;
  group_id: string;
  /** Role accountable for this rule (drives per-role scoring + remedial assignment). */
  role: CaseRole;
  order: number;
  label: string;
  description: string;
  /** Tenant notes merged into the assessment rubric block sent with the prompt. */
  guidance_notes?: string;
  /** FCA regulatory anchor, e.g. "COBS 9.2.1R — Obligation to obtain information" */
  fca_reference?: string;
  /** Internal policy source, e.g. "Client File Assessment Policy — KYC Standards" */
  internal_reference?: string;
  conditional: boolean;
  document_types: string[];
  /** Case-type slugs this rule applies to; empty = universal (every case). */
  applicable_case_types?: string[];
  rule_type?: "deterministic" | "judgement";
  grade_weight?: "area_insufficient" | "development";
  interim?: boolean;
}

export interface DocumentRequirement {
  id: string;
  case_types: string[];
  transaction_subtype?: string | null;
  document_slug: string;
  presence: "required" | "conditional";
  condition?: string;
  source?: string;
  interim?: boolean;
}

export interface ConsistencyCheck {
  id: string;
  applicable_case_types: string[];
  description: string;
  doc_a?: string;
  doc_b?: string;
  area?: string;
}

export interface ChronologyCheck {
  id: string;
  kind: "ordering" | "recency" | "version";
  description: string;
  applicable_case_types?: string[];
}

export interface CaseTypeRequirement {
  case_type: string;
  required: string[];
  recommended: string[];
}

export interface DocumentValidation {
  complete: string;
  incomplete: string;
}

/** Draft as returned by the API (no prompt_template). */
export interface RuleSetDraft {
  company_id: string;
  review_profile?: string;
  overarching_principles?: string;
  name: string;
  groups: RuleGroup[];
  rules: Rule[];
  case_type_requirements: CaseTypeRequirement[];
  /** Docs required for every case type (merged with each case-type's required at read time). */
  universal_required: string[];
  /** Docs recommended for every case type (merged with each case-type's recommended at read time). */
  universal_recommended: string[];
  document_equivalences: Record<string, string[]>;
  document_display_names: Record<string, string>;
  /** doc slug → description of what the document is and why it matters. */
  document_descriptions: Record<string, string>;
  /** doc slug → list of key fields/data the AI should extract. */
  document_extraction_hints: Record<string, string[]>;
  /** doc slug → complete/incomplete validation criteria. */
  document_validation_criteria: Record<string, DocumentValidation>;
  /** doc slug → role accountable for producing/uploading the document. */
  document_type_roles: Record<string, CaseRole>;
  /** case_type slug → description for the detector prompt. */
  case_type_definitions: Record<string, string>;
  /** case_type slug → human-readable display name, e.g. "Annual Review". */
  case_type_display_names: Record<string, string>;
  case_type_in_scope?: Record<string, boolean>;
  document_requirements?: DocumentRequirement[];
  consistency_checks?: ConsistencyCheck[];
  chronology_checks?: ChronologyCheck[];
  current_version: number | null;
  updated_at: string;
  updated_by: string;
}

export interface RuleSetVersion extends RuleSetDraft {
  version: number;
  version_label: string | null;
  published_at: string;
  published_by: string;
  is_active: boolean;
}

export interface RuleSetDraftClientUpdate {
  name: string;
  overarching_principles?: string;
  groups: RuleGroup[];
  rules: Rule[];
  case_type_requirements: CaseTypeRequirement[];
  universal_required: string[];
  universal_recommended: string[];
  document_equivalences: Record<string, string[]>;
  document_display_names: Record<string, string>;
  document_descriptions: Record<string, string>;
  document_extraction_hints: Record<string, string[]>;
  document_validation_criteria: Record<string, DocumentValidation>;
  document_type_roles: Record<string, CaseRole>;
  case_type_definitions: Record<string, string>;
  case_type_display_names: Record<string, string>;
}

export interface RuleSetVersionSummary {
  version: number;
  version_label: string | null;
  is_active: boolean;
  published_at: string;
  published_by: string;
  name: string;
}

export interface RuleSetReorderBody {
  group_orders?: { id: string; order: number }[];
  rule_orders?: { id: string; order: number; group_id?: string }[];
}

export interface RulePatchBody {
  guidance_notes?: string;
  fca_reference?: string;
  internal_reference?: string;
  label?: string;
  description?: string;
  conditional?: boolean;
  document_types?: string[];
  applicable_case_types?: string[];
  group_id?: string;
  role?: CaseRole;
  order?: number;
}

/** Body for POST .../draft/groups */
export interface RuleGroupCreateBody {
  id: string;
  name: string;
  description?: string | null;
  order?: number;
}

/** Body for PATCH .../draft/groups/{group_id} */
export interface RuleGroupPatchBody {
  name?: string;
  description?: string | null;
  order?: number;
}

/** Body for POST .../draft/rules */
export interface RuleCreateBody {
  id: string;
  group_id: string;
  role: CaseRole;
  label: string;
  description: string;
  guidance_notes?: string;
  conditional?: boolean;
  document_types?: string[];
  applicable_case_types?: string[];
  order?: number;
}

export const getRuleSetDraft = async (scope?: RuleSetProfileScope): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "GET",
    url: `/case-checking/rule-sets/draft${reviewProfileQuery(scope)}`,
  });
};

export const putRuleSetDraft = async (
  body: RuleSetDraftClientUpdate,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "PUT",
    url: `/case-checking/rule-sets/draft${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const patchRuleSetDraftGroup = async (
  groupId: string,
  body: RuleGroupPatchBody,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "PATCH",
    url: `/case-checking/rule-sets/draft/groups/${encodeURIComponent(groupId)}${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const postRuleSetDraftGroup = async (
  body: RuleGroupCreateBody,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "POST",
    url: `/case-checking/rule-sets/draft/groups${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const deleteRuleSetDraftGroup = async (
  groupId: string,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "DELETE",
    url: `/case-checking/rule-sets/draft/groups/${encodeURIComponent(groupId)}${reviewProfileQuery(scope)}`,
  });
};

export const patchRuleSetDraftRule = async (
  ruleId: string,
  body: RulePatchBody,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "PATCH",
    url: `/case-checking/rule-sets/draft/rules/${encodeURIComponent(ruleId)}${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const postRuleSetDraftRule = async (
  body: RuleCreateBody,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "POST",
    url: `/case-checking/rule-sets/draft/rules${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const deleteRuleSetDraftRule = async (
  ruleId: string,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "DELETE",
    url: `/case-checking/rule-sets/draft/rules/${encodeURIComponent(ruleId)}${reviewProfileQuery(scope)}`,
  });
};

export const upsertRuleSetDraftCaseType = async (
  caseType: string,
  body: { required: string[]; recommended: string[]; name?: string; description?: string },
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "PUT",
    url: `/case-checking/rule-sets/draft/case-types/${encodeURIComponent(caseType)}${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const patchRuleSetDraftCaseTypeMetadata = async (
  caseType: string,
  body: { name?: string; description?: string },
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "PATCH",
    url: `/case-checking/rule-sets/draft/case-types/${encodeURIComponent(caseType)}/metadata${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const deleteRuleSetDraftCaseType = async (
  caseType: string,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "DELETE",
    url: `/case-checking/rule-sets/draft/case-types/${encodeURIComponent(caseType)}${reviewProfileQuery(scope)}`,
  });
};

export const putRuleSetDraftUniversalDocs = async (
  body: { required: string[]; recommended: string[] },
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "PUT",
    url: `/case-checking/rule-sets/draft/universal-docs${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const upsertRuleSetDraftDocumentType = async (
  body: {
    slug: string;
    display_name: string;
    description?: string;
    extraction_hints?: string[];
    validation_complete?: string;
    validation_incomplete?: string;
    role?: CaseRole;
  },
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "POST",
    url: `/case-checking/rule-sets/draft/document-types${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const deleteRuleSetDraftDocumentType = async (
  slug: string,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "DELETE",
    url: `/case-checking/rule-sets/draft/document-types/${encodeURIComponent(slug)}${reviewProfileQuery(scope)}`,
  });
};

export const reorderRuleSetDraft = async (
  body: RuleSetReorderBody,
  scope?: RuleSetProfileScope,
): Promise<RuleSetDraft> => {
  return apiRequest<RuleSetDraft>({
    method: "POST",
    url: `/case-checking/rule-sets/draft/reorder${reviewProfileQuery(scope)}`,
    data: body,
  });
};

export const publishRuleSet = async (
  versionLabel?: string,
  scope?: RuleSetProfileScope,
): Promise<RuleSetVersion> => {
  const data =
    versionLabel !== undefined && versionLabel !== ""
      ? { version_label: versionLabel }
      : {};
  return apiRequest<RuleSetVersion>({
    method: "POST",
    url: `/case-checking/rule-sets/publish${reviewProfileQuery(scope)}`,
    data,
  });
};

export const listRuleSetVersions = async (
  scope?: RuleSetProfileScope,
): Promise<RuleSetVersionSummary[]> => {
  return apiRequest<RuleSetVersionSummary[]>({
    method: "GET",
    url: `/case-checking/rule-sets/versions${reviewProfileQuery(scope)}`,
  });
};

export const getRuleSetVersion = async (
  version: number,
  scope?: RuleSetProfileScope,
): Promise<RuleSetVersion> => {
  return apiRequest<RuleSetVersion>({
    method: "GET",
    url: `/case-checking/rule-sets/versions/${version}${reviewProfileQuery(scope)}`,
  });
};

/**
 * Return the currently active published rule set for the user's company.
 * Resolves to `null` if the company has never published.
 */
export const getActiveRuleSetVersion = async (): Promise<RuleSetVersion | null> => {
  try {
    return await apiRequest<RuleSetVersion>({
      method: "GET",
      url: "/case-checking/rule-sets/versions/active",
    });
  } catch (e) {
    const status = (e as { status?: number; response?: { status?: number } })?.status
      ?? (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw e;
  }
};

export const activateRuleSetVersion = async (
  version: number,
  scope?: RuleSetProfileScope,
): Promise<RuleSetVersion> => {
  return apiRequest<RuleSetVersion>({
    method: "POST",
    url: `/case-checking/rule-sets/versions/${version}/activate${reviewProfileQuery(scope)}`,
  });
};

/** Download rules audit template (required documents + rules) for the active published rule set. */
export const downloadRulesAuditTemplateXlsx = async (
  scope?: RuleSetProfileScope,
  fallbackName: string = "rules_audit_template.xlsx"
): Promise<void> => {
  const response = await apiRequestWithHeaders<Blob>({
    method: "GET",
    url: `/case-checking/rule-sets/export/rules-audit-xlsx${reviewProfileQuery(scope)}`,
    responseType: "blob",
  });
  let filename = fallbackName;
  const disposition = response.headers["content-disposition"] as string | undefined;
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/i);
    if (match?.[1]) filename = match[1];
  }
  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
